import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import {Client} from '../../models/Client';
import {NewClientFormProps} from "../../components/clients/NewClientScreen";

interface ClientsState {
    clients: Client[];
    topClients: any[];
    userTopClients: any[];
    currentClient: Client | null;
    getClientsStatus: RequestStatus;
    getTopClientsStatus: RequestStatus;
    createClientStatus: RequestStatus;
    updateClientStatus: RequestStatus;
    deleteClientStatus: RequestStatus;
    getCurrentClientStatus: RequestStatus;
    errors: string[];
    currentPage: number;
    totalPages: number;
    total: number;
}

interface GetClientsProps {
    search: string;
}

const initialState: ClientsState = {
    clients: [],
    topClients: [],
    userTopClients: [],
    currentClient: null,
    getClientsStatus: RequestStatus.Idle,
    getTopClientsStatus: RequestStatus.Idle,
    createClientStatus: RequestStatus.Idle,
    updateClientStatus: RequestStatus.Idle,
    deleteClientStatus: RequestStatus.Idle,
    getCurrentClientStatus: RequestStatus.Idle,
    errors: [],
    currentPage: 1,
    totalPages: 1,
    total: 0
};

export const getClients = createAsyncThunk(
    'clients/getClients',
    async (_, { rejectWithValue, getState }) => {
        const urlParams = new URLSearchParams(window.location.search);
        const page = urlParams.get('p') || 1;
        const search = encodeURIComponent(urlParams.get('search') || '');

        const response = await apiClient.get(`clients?page=${page}&search=${search}`);
        return response.data;
    }
);

export const createClient = createAsyncThunk('clients/createClient', async (clientData: NewClientFormProps, {rejectWithValue}) => {
    const response = await apiClient.post('clients', clientData);
    return response.data;
});

export const updateClient = createAsyncThunk(
    'clients/updateClient',
    async ({id, clientData}: { id: string | undefined; clientData: any }, {rejectWithValue}) => {
        const response = await apiClient.put(`clients/${id}`, clientData);
        return response.data;
    }
);

export const deleteClient = createAsyncThunk('clients/deleteClient', async ({id}: { id: string | undefined }, {rejectWithValue}) => {
    const response = await apiClient.delete(`clients/${id}`);
    return response.data;
});

export const getCurrentClient = createAsyncThunk(
    'clients/getCurrentClient',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`clients/${id}`);
        return response.data;
    }
);

export const getTopClients = createAsyncThunk('clients/getTopClients', async (_, {rejectWithValue}) => {

    const searchParams = new URLSearchParams(window.location.search);
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');
    const perPage = searchParams.get('perPage');

    const url = `clients/top?fromDate=${fromDate}&toDate=${toDate}&perPage=${perPage}`;

    const response = await apiClient.get(url);
    return response.data;

});

const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        resetCreateClientStatus: (state) => {
            state.createClientStatus = RequestStatus.Idle;
            state.currentClient = null;
        },
        resetUpdateClientStatus: (state) => {
            state.updateClientStatus = RequestStatus.Idle;
            state.currentClient = null;
        },
        resetDeleteClientStatus: (state) => {
            state.deleteClientStatus = RequestStatus.Idle;
            state.currentClient = null;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getClients.pending, (state) => {
                state.getClientsStatus = RequestStatus.Loading;
            })
            .addCase(getClients.fulfilled, (state, action) => {
                console.log('Success', action.payload);
                state.getClientsStatus = RequestStatus.Success;
                state.clients = action.payload.data;
                state.totalPages = action.payload.last_page;
                state.total = action.payload.total;
            })
            .addCase(getClients.rejected, (state) => {
                state.getClientsStatus = RequestStatus.Failed;
            })
            .addCase(createClient.pending, (state, action) => {
                state.createClientStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.currentClient = action.payload;
                state.createClientStatus = RequestStatus.Success;
            })
            .addCase(createClient.rejected, (state, action) => {
                state.createClientStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(updateClient.pending, (state, action) => {
                state.updateClientStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateClient.fulfilled, (state) => {
                state.updateClientStatus = RequestStatus.Success;
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.updateClientStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(deleteClient.pending, (state) => {
                state.deleteClientStatus = RequestStatus.Loading;
            })
            .addCase(deleteClient.fulfilled, (state) => {
                state.deleteClientStatus = RequestStatus.Success;
            })
            .addCase(deleteClient.rejected, (state) => {
                state.deleteClientStatus = RequestStatus.Failed;
            })
            .addCase(getCurrentClient.pending, (state) => {
                state.getCurrentClientStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentClient.fulfilled, (state, action) => {
                const clientData = action.payload;
                state.currentClient = {
                    ...clientData,
                };
                state.getCurrentClientStatus = RequestStatus.Success;
            })
            .addCase(getCurrentClient.rejected, (state) => {
                state.getCurrentClientStatus = RequestStatus.Failed;
            })

            .addCase(getTopClients.pending, (state) => {
                state.getTopClientsStatus = RequestStatus.Loading;
            })
            .addCase(getTopClients.fulfilled, (state, action) => {
                state.getTopClientsStatus = RequestStatus.Success;
                state.topClients = action.payload.clients;
                state.userTopClients = action.payload.users;
            })
            .addCase(getTopClients.rejected, (state) => {
                state.getTopClientsStatus = RequestStatus.Failed;
            });
    },
});


export const {
    resetCreateClientStatus,
    resetUpdateClientStatus,
    resetDeleteClientStatus,
} = clientsSlice.actions;

export default clientsSlice.reducer;